import type ServiceApi from "../../api";
import type {LocationPdfType, PdfOrientation, ValuationPdfType} from "../../redux/pdfConstants";
import {RevaApi} from "../api-constants";
import {FileDownloadApiInterface} from "./file-download";

/**
 * Common base for the PDFs reva-rest renders for one valuation
 * (GET {@link RevaApi.PATH_REVA_PDF}/{revaId}/…). Subclasses contribute the segments after the
 * valuation id and, if needed, query parameters.
 */
abstract class ValuationDocumentApiInterface extends FileDownloadApiInterface {
    protected constructor(protected readonly revaId: string) {
        super();
    }

    /** Path segments appended after the valuation id. */
    protected abstract documentPathSegments(): string[];

    /** Query parameters of the request; undefined values are left out of the URL. */
    protected queryParams(): Record<string, string | undefined> | undefined {
        return undefined;
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl(
            [RevaApi.PATH_REVA_PDF, this.revaId, ...this.documentPathSegments()],
            this.queryParams(),
        );
    }
}

/**
 * API interface for one of the three valuation reports of the live valuation
 * (GET {@link RevaApi.PATH_REVA_PDF}/{revaId}/{type}).
 */
export class ValuationPdfApiInterface extends ValuationDocumentApiInterface {
    constructor(
        revaId: string,
        private readonly type: ValuationPdfType,
    ) {
        super(revaId);
    }

    protected documentPathSegments(): string[] {
        return [this.type];
    }
}

/**
 * API interface for one of the three valuation reports rendered from a stored lifecycle snapshot
 * (GET {@link RevaApi.PATH_REVA_PDF}/{revaId}/{@link RevaApi.PATH_VALUATION_SNAPSHOTS}/{number}/{type}).
 * reva-rest creates a missing snapshot PDF on the way and archives it in the order.
 */
export class SnapshotPdfApiInterface extends ValuationDocumentApiInterface {
    constructor(
        revaId: string,
        private readonly snapshotNumber: number,
        private readonly type: ValuationPdfType,
    ) {
        super(revaId);
    }

    protected documentPathSegments(): string[] {
        return [RevaApi.PATH_VALUATION_SNAPSHOTS, String(this.snapshotNumber), this.type];
    }
}

/**
 * API interface for one of the map/location PDFs
 * (GET {@link RevaApi.PATH_REVA_PDF}/{revaId}/{type}[?orientation=…]).
 * Only the types with a map crop carry an orientation; without one reva-pdf uses its own default.
 */
export class LocationPdfApiInterface extends ValuationDocumentApiInterface {
    constructor(
        revaId: string,
        private readonly type: LocationPdfType,
        private readonly orientation?: PdfOrientation,
    ) {
        super(revaId);
    }

    protected documentPathSegments(): string[] {
        return [this.type];
    }

    protected queryParams(): Record<string, string | undefined> {
        return {orientation: this.orientation};
    }
}
